// apps/provider/src/services/api/planOfCare.js
// Plan of Care CRUD and lifecycle methods
//
// Methods: getPlansOfCare, getPlanOfCare, createPlanOfCare, updatePlanOfCare,
//          deletePlanOfCare, getPlanOfCareVersions, createPlanOfCareVersion
//
// Response types: @prism-ehr/shared → PlanOfCareResponse

const _inFlightPoc = new Map();

/**
 * @returns {Object} Plan of Care domain methods (bound to `this` of the composed apiService)
 */
export function createPlanOfCareMethods() {
  return {
    /**
     * Get plans of care with filters.
     * Concurrent calls with identical params share one in-flight promise.
     *
     * Xano: GET /plan-of-care
     *
     * @param {Object} [filters]
     * @param {string} [filters.patient_id]
     * @param {string} [filters.organization_id]
     * @param {number} [filters.case_id]
     * @param {string} [filters.provider_id]
     * @param {string} [filters.status]
     * @returns {Promise<{ items: import('@prism-ehr/shared').PlanOfCareResponse[], curPage: number, nextPage: number, prevPage: number, pageSize: number }>}
     */
    async getPlansOfCare(filters = {}) {
      const queryParams = new URLSearchParams();

      if (filters.patient_id) queryParams.append('patient_id', filters.patient_id);
      if (filters.organization_id) queryParams.append('organization_id', filters.organization_id);
      if (filters.case_id) queryParams.append('case_id', filters.case_id);
      if (filters.case_ids) queryParams.append('case_ids', filters.case_ids);
      if (filters.provider_id) queryParams.append('provider_id', filters.provider_id);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.start_date) queryParams.append('start_date', filters.start_date);
      if (filters.end_date) queryParams.append('end_date', filters.end_date);
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);
      if (filters.query) queryParams.append('query', filters.query);

      const key = queryParams.toString();
      if (_inFlightPoc.has(key)) return _inFlightPoc.get(key);

      const promise = this.request(`/plan-of-care?${key}`, {
        method: 'GET'
      }).finally(() => _inFlightPoc.delete(key));

      _inFlightPoc.set(key, promise);
      return promise;
    },

    /**
     * Get a single plan of care by ID.
     *
     * Xano: GET /plan-of-care/{plan_id}
     *
     * @param {string} planId
     * @returns {Promise<import('@prism-ehr/shared').PlanOfCareResponse>}
     */
    async getPlanOfCare(planId) {
      return this.request(`/plan-of-care/${planId}`, {
        method: 'GET'
      });
    },

    /**
     * Create a new plan of care.
     *
     * Xano: POST /plan-of-care
     *
     * @param {Object} planData
     * @returns {Promise<import('@prism-ehr/shared').PlanOfCareResponse>}
     */
    async createPlanOfCare(planData) {
      return this.request('/plan-of-care', {
        method: 'POST',
        body: JSON.stringify(planData)
      }, 'createPlanOfCare');
    },

    /**
     * Update a plan of care.
     *
     * Xano: PATCH /plan-of-care/{plan_id}
     *
     * @param {string} planId
     * @param {Object} planData
     * @returns {Promise<import('@prism-ehr/shared').PlanOfCareResponse>}
     */
    async updatePlanOfCare(planId, planData) {
      return this.request(`/plan-of-care/${planId}`, {
        method: 'PATCH',
        body: JSON.stringify(planData)
      }, 'updatePlanOfCare');
    },

    /**
     * Delete a plan of care.
     *
     * Xano: DELETE /plan-of-care/{plan_id}
     *
     * @param {string} planId
     * @returns {Promise<null>}
     */
    async deletePlanOfCare(planId) {
      return this.request(`/plan-of-care/${planId}`, {
        method: 'DELETE'
      }, 'deletePlanOfCare');
    },

    // ── POC Version History ────────────────────────────────────────

    /**
     * Get version history for a plan of care.
     *
     * Xano: GET /plan-of-care-version?plan_of_care_id=X
     * Xano staging file: plan_of_care_version_GET.xs
     *
     * @param {string} planOfCareId
     * @returns {Promise<Object[]>}
     */
    async getPlanOfCareVersions(planOfCareId) {
      return this.request(`/plan-of-care-version?plan_of_care_id=${encodeURIComponent(planOfCareId)}`, {
        method: 'GET'
      });
    },

    /**
     * Create a POC version snapshot (called before updating a POC).
     *
     * Xano: POST /plan-of-care-version
     * Xano staging file: plan_of_care_version_POST.xs
     *
     * @param {Object} data
     * @param {string} data.plan_of_care_id
     * @param {number} data.version
     * @param {Object} data.snapshot_json - Full POC state before update
     * @param {string} data.source_note_id - The note that triggered this version
     * @returns {Promise<Object>}
     */
    async createPlanOfCareVersion(data) {
      return this.request('/plan-of-care-version', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },
  };
}
